import { FiMessageCircle } from 'react-icons/fi';

export const idiomsData = {
  courseId: 'idioms',
  courseType: 'vocabulary',
  title: 'Idioms & Expressions',
  icon: FiMessageCircle,
  overview: {
    impact: 'Sound more natural in Speaking',
    focus: 'Common English idioms',
    uses: 'Speaking (carefully), Listening',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'What Are Idioms?',
      content: `<p>Idioms are expressions whose meaning differs from the literal meaning of the words. Using them naturally can improve your Speaking score.</p>`,
      warning: 'Don\'t force idioms! Only use them if they fit naturally. Overusing idioms sounds unnatural.',
    },
    {
      title: 'Common IELTS-Friendly Idioms',
      content: `<p>These are safe to use in IELTS Speaking:</p>`,
      table: {
        headers: ['Idiom', 'Meaning', 'Example'],
        rows: [
          ['at the end of the day', 'ultimately', 'At the end of the day, health is most important.'],
          ['pros and cons', 'advantages and disadvantages', 'Let me discuss the pros and cons.'],
          ['once in a while', 'occasionally', 'I go to the cinema once in a while.'],
          ['hit the books', 'study hard', 'I need to hit the books for my exams.'],
          ['a piece of cake', 'very easy', 'The reading test was a piece of cake.'],
        ],
      },
    },
    {
      title: 'Idioms to Avoid',
      content: `<p>Avoid these in formal IELTS Writing:</p>`,
      points: [
        'Slang expressions (cool, awesome, stuff)',
        'Very informal idioms (kick the bucket, break a leg)',
        'Culture-specific idioms that may confuse',
        'Any idiom you\'re not 100% sure about',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match the Meaning',
      type: 'select',
      instructions: 'Choose the correct meaning of each idiom.',
      questions: [
        {
          text: '"Once in a blue moon"',
          options: ['Very rarely', 'At night', 'Every month'],
          answer: 'Very rarely',
          explanation: 'This idiom means something happens very infrequently.',
        },
        {
          text: '"Break the ice"',
          options: ['Start a conversation', 'Freeze something', 'End a relationship'],
          answer: 'Start a conversation',
          explanation: 'To break the ice means to make people feel more comfortable in a social situation.',
        },
        {
          text: '"Keep an eye on"',
          options: ['Watch carefully', 'Ignore completely', 'Look away'],
          answer: 'Watch carefully',
          explanation: 'To keep an eye on means to monitor or watch something carefully.',
        },
        {
          text: '"At the end of the day"',
          options: ['Ultimately/finally', 'In the evening', 'Before sleeping'],
          answer: 'Ultimately/finally',
          explanation: 'This means "when everything is considered" or "ultimately".',
        },
        {
          text: '"A piece of cake"',
          options: ['Very easy', 'Delicious food', 'A small portion'],
          answer: 'Very easy',
          explanation: 'Something that is a piece of cake is very easy to do.',
        },
        {
          text: '"Hit the books"',
          options: ['Study hard', 'Destroy books', 'Go to library'],
          answer: 'Study hard',
          explanation: 'To hit the books means to study intensively.',
        },
      ],
    },
    {
      title: 'Complete the Idiom',
      type: 'fill-in',
      instructions: 'Type the missing word to complete the idiom.',
      questions: [
        {
          text: 'I need to weigh the pros and ___ before making a decision.',
          answer: 'cons',
          explanation: '"Pros and cons" means advantages and disadvantages.',
        },
        {
          text: 'At the end of the ___, what matters most is your health.',
          answer: 'day',
          explanation: '"At the end of the day" means ultimately or in the final analysis.',
        },
        {
          text: 'I only go to the gym once in a ___ moon.',
          answer: 'blue',
          explanation: '"Once in a blue moon" means very rarely.',
        },
        {
          text: 'Learning to drive was a piece of ___.',
          answer: 'cake',
          explanation: '"A piece of cake" means something very easy.',
        },
      ],
    },
    {
      title: 'Use in Context',
      type: 'select',
      instructions: 'Which idiom best fits each situation?',
      questions: [
        {
          text: 'You want to say the exam was very easy.',
          options: ['It was a piece of cake', 'I hit the books', 'Once in a blue moon'],
          answer: 'It was a piece of cake',
          explanation: '"A piece of cake" describes something very easy.',
        },
        {
          text: 'You want to say you need to study hard tonight.',
          options: ['I need to hit the books', 'Break the ice', 'Pros and cons'],
          answer: 'I need to hit the books',
          explanation: '"Hit the books" means to study hard.',
        },
        {
          text: 'You want to start a conversation at a party where no one knows each other.',
          options: ['Break the ice', 'Keep an eye on', 'At the end of the day'],
          answer: 'Break the ice',
          explanation: '"Break the ice" means to start conversation in an awkward social situation.',
        },
      ],
    },
    {
      title: 'Appropriate or Not?',
      type: 'select',
      instructions: 'Is this idiom appropriate for IELTS?',
      questions: [
        {
          text: 'Using "pros and cons" in Speaking Part 3',
          options: ['Appropriate', 'Not appropriate'],
          answer: 'Appropriate',
          explanation: '"Pros and cons" is a safe, academic-sounding idiom for IELTS.',
        },
        {
          text: 'Using "kick the bucket" in Writing Task 2',
          options: ['Not appropriate', 'Appropriate'],
          answer: 'Not appropriate',
          explanation: 'This is too informal/slang for academic writing. Say "pass away" instead.',
        },
        {
          text: 'Using "at the end of the day" in Speaking',
          options: ['Appropriate', 'Not appropriate'],
          answer: 'Appropriate',
          explanation: 'This is a natural, common idiom suitable for IELTS Speaking.',
        },
        {
          text: 'Using "cool" to mean "good" in Writing Task 2',
          options: ['Not appropriate', 'Appropriate'],
          answer: 'Not appropriate',
          explanation: '"Cool" is slang and too informal for academic writing.',
        },
      ],
    },
    {
      title: 'Speaking Practice',
      type: 'writing',
      instructions: 'Write a sentence using each idiom naturally.',
      questions: [
        {
          text: 'Use "at the end of the day" in a sentence about education.',
          example: 'At the end of the day, the quality of education depends on dedicated teachers.',
          tip: 'Use this to introduce a final, important point.',
        },
        {
          text: 'Use "pros and cons" in a sentence about technology.',
          example: 'There are several pros and cons to consider when discussing social media.',
          tip: 'This is perfect for discussing advantages and disadvantages.',
        },
        {
          text: 'Use "once in a while" in a sentence about hobbies.',
          example: 'I enjoy going hiking once in a while to escape the city.',
          tip: '"Once in a while" means occasionally—more common than "once in a blue moon".',
        },
      ],
    },
  ],
  tips: [
    'Only use idioms you fully understand',
    'Use 1-2 idioms naturally in Speaking, not more',
    'Avoid idioms in formal Writing (Task 2)',
    'Listen for idioms in podcasts and movies',
  ],
  dailyPractice: [
    'Learn one new idiom per day with context',
    'Practice using idioms in conversations',
    'Watch English shows and note idioms used',
    'Test yourself on idiom meanings',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Idioms & Expressions: Natural English idioms for IELTS Speaking, when to use them, and which to avoid.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...idiomsData,
      title: idiomsData.title + ' (Foundations)',
      overview: {
        ...idiomsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: idiomsData.sections.slice(0, Math.max(2, Math.ceil(idiomsData.sections.length / 2))),
      exercises: idiomsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...idiomsData,
      title: idiomsData.title + ' (Advanced)',
      overview: {
        ...idiomsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: idiomsData.sections,
      exercises: idiomsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(idiomsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return idiomsData;
}
